import React, { useCallback, useContext, useMemo, useState } from 'react'

import { Modal, Input, Button, Radio, Tabs, Tooltip } from 'antd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSave, faTimes } from '@fortawesome/pro-solid-svg-icons'
import { isFunction } from 'lodash'
import { useSelector } from 'react-redux'

import SmartTable, { SmartTableDataTypes } from '../../shared/SmartTable'
import { sendInternshipMessage } from '../../../utils/api/internshipMessage'
import { onError, onSuccess } from '../../../utils/apiHelper'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { selectUser } from '../../../reducers/UserReducer'
import useInternship from '../../../utils/api/hooks/useInternship'
import { InstitutionContext } from '../../../Providers/InstitutionProvider'
import { createRejectionReason, deleteRejectionReason, updateRejectionReason } from '../../../utils/api/rejectionReason'
import { isObject } from '../../../utils'
import { isNurse } from '../../../utils/roles'

const RadioGroup = Radio.Group
const TabPane = Tabs.TabPane

const RADIO_MAX_WIDTH = '450px'

const InternshipRejection = ({ internship, onRejection, onClose }) => {
  const ic = useContext(InstitutionContext)

  const institution = useMemo(() => ic?.institution ?? null, [ic?.institution])
  const rejectionReasons = useMemo(() => ic?.rejectionReasons ?? [], [ic?.rejectionReasons])
  const setRejectionReasons = useCallback(reasons => {
    if (isObject(ic) && isFunction(ic?.setRejectionReasons)) {
      ic.setRejectionReasons(reasons)
    }
  }, [ic?.setRejectionReasons])

  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [selectedReason, setSelectedReason] = useState(0)
  const [comment, setComment] = useState('')
  const [loading, setLoading] = useState(false)

  const internshipHandler = useInternship({ callback: onRejection })

  const handleDatacreation = (reason) => {
    createRejectionReason(user, { institution: institution.id, name: reason.name }).then(({ data }) => {
      setRejectionReasons(oldReasons => [...oldReasons, data])
      onSuccess(t('rejection_reason.create.success'))
    }).catch((error) => {
      onError(t('rejection_reason.create.error'))
      console.error(error)
    })
  }

  const handleDataEdition = (reason) => {
    updateRejectionReason(user, reason, { name: reason.name }).then(({ data }) => {
      setRejectionReasons(oldReasons => oldReasons.map(r => (r.id === data.id ? { ...r, name: data.name } : r)))
      onSuccess(t('rejection_reason.update.success'))
    }).catch((error) => {
      onError(t('rejection_reason.update.error'))
      console.error(error)
    })
  }

  const handleDataDeletion = (reason) => {
    deleteRejectionReason(user, reason).then(() => {
      setRejectionReasons(oldReasons => oldReasons.filter(r => r.id !== reason.id))
      onSuccess(t('rejection_reason.delete.success'))
    }).catch((error) => {
      onError(t('rejection_reason.delete.error'))
      console.error(error)
    })
  }

  const handleCancel = (rejectionApproved = false) => {
    if (isFunction(onClose)) {
      onClose(rejectionApproved)
    }
  }

  const handleInternshipRejection = async () => {
    if (selectedReason === 0 && comment.length === 0) {
      onError(t('Please complete your rejection reason.'))
      return
    }

    setLoading(true)

    const reasons = []

    if (selectedReason !== 0) {
      reasons.push(selectedReason)
    }

    internshipHandler.refuse(internship, reasons, comment).then(() => {
      let translatedComment = t('Internship refused') + ' : '

      if (selectedReason === 0) {
        translatedComment += comment
      } else {
        translatedComment += rejectionReasons.filter(rr => rr.id === selectedReason)[0].name
      }

      sendInternshipMessage(user, { internship: internship.id, content: translatedComment }).then(() => {
        setLoading(false)
        handleCancel(true)
      })
    })
  }

  const renderRejectionReasons = () => {
    const reasons = rejectionReasons.map(rr => {
      return (
        <Radio
          key={rr.id}
          style={{
            display: 'block',
            overflow: 'hidden',
            maxWidth: RADIO_MAX_WIDTH,
            textOverflow: 'ellipsis'
          }}
          value={rr.id}
        >
          <Tooltip placement='left' title={rr.name}>
            {rr.name}
          </Tooltip>
        </Radio>
      )
    })

    return (
      <div>
        {reasons}
        <Radio style={{ display: 'block' }} value={0}>
          <Input
            placeholder={t('Other')}
            value={comment}
            onChange={v => setComment(v.target.value)}
          />
        </Radio>
      </div>
    )
  }

  const columns = [
    { type: SmartTableDataTypes.ID, key: 'id' },
    { type: SmartTableDataTypes.STRING, name: t('Reason'), key: 'name' }
  ]
  const rejections = useMemo(() => rejectionReasons.map(s => ({
    id: s.id,
    name: s.name
  })), [rejectionReasons])

  return (
    <div>
      <Modal
        title={t('Refuse an internship')}
        visible={internship != null}
        onCancel={() => { handleCancel() }}
        footer={[
          <Button onClick={() => { handleCancel() }} key='cancel' type='default' loading={loading}>
            <FontAwesomeIcon icon={faTimes} />&nbsp;{t('Cancel')}
          </Button>,
          <Button onClick={handleInternshipRejection} key='save' type='primary' loading={loading}>
            <FontAwesomeIcon icon={faSave} />&nbsp;{t('Save')}
          </Button>
        ]}
      >
        <Tabs defaultActiveKey='1' onChange={null} animated={false}>
          <TabPane tab={t('Refuse an internship')} key='1'>
            {t('Please select a reason to refuse this internship')} :
            <div className='v-spacing' />
            <RadioGroup onChange={v => setSelectedReason(v.target.value)} value={selectedReason}>
              {renderRejectionReasons()}
            </RadioGroup>
            {rejectionReasons.length === 0 && (
              <div style={{ marginTop: '30px', color: '#9E9E9E' }}>
                {t('You can add rejection reasons in the second tab.')}
              </div>
            )}
          </TabPane>
          {!isNurse(user) && (
            <TabPane tab={t('Rejection reasons')} key='2'>
              <SmartTable
                columns={columns}
                data={rejections}
                loading={ic?.loading}
                noDataText={t('You do not have any internship rejection reason.')}
                onDataAdd={handleDatacreation}
                onDataDelete={handleDataDeletion}
                onDataEdit={handleDataEdition}
              />
            </TabPane>
          )}
        </Tabs>
      </Modal>
    </div>
  )
}

export default InternshipRejection
